import { ComparisonOperator, type DurationColumnFormat } from '@blaze-shared/api-contracts-domain';
import { isDatasetLinkValue } from '@blaze-shared/schema-domain';
import {
  isDurationEqWithFormat,
  isDurationGtWithFormat,
  isDurationLtWithFormat,
} from '@blaze-shared/shared-feature';
import {
  isDateEq,
  isNullish,
  isTime,
  isTimeEq,
  isTimeGt,
  isTimeLt,
  isValidStringDate,
} from '@blaze-shared/shared-util';

export function evaluateExpression(
  operator: string,
  columnValue: any,
  targetValue: any,
  durationFormatForCol?: DurationColumnFormat | boolean,
): boolean | undefined {
  switch (operator) {
    case ComparisonOperator.ContainsText:
      return containsText(columnValue, targetValue);
    case ComparisonOperator.Eq:
      return eq(columnValue, targetValue, durationFormatForCol);
    case ComparisonOperator.EqIgnoreCase:
      return eqIgnoreCase(columnValue, targetValue, durationFormatForCol);
    case ComparisonOperator.Gt:
      return gt(columnValue, targetValue, durationFormatForCol);
    case ComparisonOperator.Gte:
      return gte(columnValue, targetValue, durationFormatForCol);
    case ComparisonOperator.HasPrefix:
      return hasPrefix(columnValue, targetValue);
    case ComparisonOperator.HasPrefixIgnoreCase:
      return hasPrefixIgnoreCase(columnValue, targetValue);
    case ComparisonOperator.In:
      return isIn(columnValue, targetValue);
    case ComparisonOperator.IsFalse:
      return isFalse(columnValue);
    case ComparisonOperator.IsNotNull:
      return isNotNull(columnValue);
    case ComparisonOperator.IsNull:
      return isNull(columnValue);
    case ComparisonOperator.IsTrue:
      return isTrue(columnValue);
    case ComparisonOperator.Lt:
      return lt(columnValue, targetValue, durationFormatForCol);
    case ComparisonOperator.Lte:
      return lte(columnValue, targetValue, durationFormatForCol);
    case ComparisonOperator.Neq:
      return neq(columnValue, targetValue, durationFormatForCol);
    case ComparisonOperator.NotIn:
      return notIn(columnValue, targetValue);
    case 'ContainsEvery':
      return containsEvery(columnValue, targetValue);
    case 'DoesNotContain':
      return doesNotContain(columnValue, targetValue);
    case ComparisonOperator.Near:
    default:
      console.warn(`Expression Evaluation: Operator: ${operator} not executable`);
      return false;
  }
}

export function containsText(columnValue: any, targetValue: any): boolean | undefined {
  if (typeof columnValue === 'string' && typeof targetValue === 'string') {
    return columnValue.toLowerCase().includes(targetValue.toLowerCase());
  }

  if (Array.isArray(columnValue) && typeof targetValue === 'string') {
    return columnValue.join().toLowerCase().includes(targetValue.toLowerCase());
  }

  return false;
}
export function eq(
  columnValue: any,
  targetValue: any,
  durationFormatForCol?: boolean | DurationColumnFormat,
): boolean | undefined {
  // Primitive equality is needed because for example Intelli values arrive as strings while columns may still be numeric
  if (columnValue == targetValue) {
    return true;
  }

  const checkIsBoolean =
    typeof columnValue === 'boolean' && JSON.stringify(columnValue) === targetValue;
  const isEqualObject =
    typeof columnValue === 'object' && JSON.stringify(columnValue) === JSON.stringify(targetValue);

  if (checkIsBoolean || isEqualObject) {
    return true;
  } else if (isValidStringDate(columnValue) && isValidStringDate(targetValue)) {
    return isDateEq(columnValue, targetValue);
  } else if (!durationFormatForCol && isTime(columnValue) && isTime(targetValue)) {
    return isTimeEq(columnValue, targetValue);
  } else if (durationFormatForCol) {
    return isDurationEqWithFormat(columnValue, targetValue);
  }

  return false;
}
export function eqIgnoreCase(
  columnValue: any,
  targetValue: any,
  durationFormatForCol?: DurationColumnFormat | boolean,
): boolean | undefined {
  if (typeof columnValue === 'boolean') {
    return columnValue.toString?.() == targetValue?.toString?.();
  }

  if (typeof columnValue === 'string' && typeof targetValue === 'string') {
    return columnValue.toLowerCase() == targetValue.toLowerCase();
  }

  // objects
  if (JSON.stringify(columnValue)?.toLowerCase() == JSON.stringify(targetValue)?.toLowerCase()) {
    return true;
  }

  if (isValidStringDate(columnValue) && isValidStringDate(targetValue)) {
    return isDateEq(columnValue, targetValue);
  } else if (isTime(columnValue) && isTime(targetValue)) {
    return isTimeEq(columnValue, targetValue);
  } else if (durationFormatForCol) {
    return isDurationEqWithFormat(columnValue, targetValue);
  }

  return false;
}
export function gt(
  columnValue: any,
  targetValue: any,
  durationFormatForCol?: DurationColumnFormat | boolean,
): boolean | undefined {
  if (!durationFormatForCol && columnValue > targetValue) {
    return true;
  }
  if (durationFormatForCol) {
    return isDurationGtWithFormat(columnValue, targetValue);
  } else if (isTime(columnValue) && isTime(targetValue)) {
    return isTimeGt(columnValue, targetValue);
  }

  return false;
}
export function gte(
  columnValue: any,
  targetValue: any,
  durationFormatForCol?: DurationColumnFormat | boolean,
): boolean | undefined {
  if (!durationFormatForCol && columnValue >= targetValue) {
    return true;
  }
  if (durationFormatForCol) {
    return (
      isDurationEqWithFormat(columnValue, targetValue) ||
      isDurationGtWithFormat(columnValue, targetValue)
    );
  } else if (isTime(columnValue) && isTime(targetValue)) {
    return isTimeGt(columnValue, targetValue) || isTimeEq(columnValue, targetValue);
  }

  return false;
}
export function hasPrefix(columnValue: any, targetValue: any): boolean | undefined {
  return columnValue?.startsWith?.(targetValue);
}
export function hasPrefixIgnoreCase(columnValue: any, targetValue: any): boolean | undefined {
  return columnValue?.toLowerCase?.()?.startsWith(targetValue?.toLowerCase?.());
}

function columnValueMatchesTargetEntry(columnValue: unknown, targetEntry: unknown): boolean {
  if (Array.isArray(columnValue)) {
    if (Array.isArray(targetEntry)) {
      return columnValue.every((value) => targetEntry.includes(value));
    }
    return columnValue.includes(targetEntry);
  }
  return targetEntry?.toString?.() === columnValue?.toString?.();
}

export function isIn(columnValue: any, targetValue: any): boolean {
  if (Array.isArray(targetValue)) {
    if (Number.isNaN(columnValue) || columnValue === 'NaN') {
      return targetValue.some((tv) => tv === 'NaN' || Number.isNaN(tv));
    }

    if (columnValue === null || columnValue === undefined || typeof columnValue !== 'object') {
      return (
        targetValue.includes(columnValue) ||
        // here we rely on the fact that .find can return a "falsy" value
        !!targetValue.find((tv) => {
          return (
            (isDatasetLinkValue(tv) && (tv.rowId === columnValue || tv.display === columnValue)) ||
            tv?.toString() === columnValue?.toString()
          );
        })
      );
    }

    return targetValue.some((tv) => columnValueMatchesTargetEntry(columnValue, tv));
  }

  if (targetValue === Object(targetValue)) {
    return columnValue in targetValue;
  }

  if (targetValue?.toString().includes(columnValue)) {
    // string
    return true;
  }

  return false;
}
export function isFalse(columnValue: any): boolean | undefined {
  return columnValue == false;
}
export function isNotNull(columnValue: any): boolean | undefined {
  if (Array.isArray(columnValue)) {
    return columnValue.length > 0;
  } else if (!Array.isArray(columnValue)) {
    return columnValue !== null && columnValue !== undefined && columnValue !== ''; // other than array
  }

  return false;
}
export function isNull(columnValue: any): boolean | undefined {
  if (columnValue === null || columnValue === undefined || columnValue === '') {
    return true;
  } else if (Array.isArray(columnValue)) {
    return !columnValue.length;
  }

  return false;
}
export function isTrue(columnValue: any): boolean | undefined {
  return columnValue == true;
}
export function lt(
  columnValue: any,
  targetValue: any,
  durationFormatForCol?: DurationColumnFormat | boolean,
): boolean | undefined {
  if (isNullish(columnValue)) {
    return false;
  }

  if (!durationFormatForCol && columnValue < targetValue) {
    return true;
  }

  if (isTime(columnValue) && isTime(targetValue)) {
    return isTimeLt(columnValue, targetValue);
  } else if (durationFormatForCol) {
    return isDurationLtWithFormat(columnValue, targetValue);
  }

  return false;
}
export function lte(
  columnValue: any,
  targetValue: any,
  durationFormatForCol?: DurationColumnFormat | boolean,
): boolean | undefined {
  if (isNullish(columnValue)) {
    return false;
  }

  if (!durationFormatForCol && columnValue <= targetValue) {
    return true;
  }

  if (isTime(columnValue) && isTime(targetValue)) {
    return isTimeLt(columnValue, targetValue) || isTimeEq(columnValue, targetValue);
  } else if (durationFormatForCol) {
    return (
      isDurationEqWithFormat(columnValue, targetValue) ||
      isDurationLtWithFormat(columnValue, targetValue)
    );
  }

  return false;
}
export function neq(
  columnValue: any,
  targetValue: any,
  durationFormatForCol?: DurationColumnFormat | boolean,
): boolean | undefined {
  if (typeof columnValue !== 'boolean' && columnValue != targetValue) {
    return true;
  } else if (
    typeof columnValue === 'boolean' &&
    columnValue?.toString?.() != targetValue?.toString?.()
  ) {
    return columnValue?.toString?.() != targetValue?.toString?.();
  }

  if (isValidStringDate(columnValue) && isValidStringDate(targetValue)) {
    return !isDateEq(columnValue, targetValue);
  } else if (isTime(columnValue) && isTime(targetValue)) {
    return !isTimeEq(columnValue, targetValue);
  } else if (durationFormatForCol) {
    return !isDurationEqWithFormat(columnValue, targetValue);
  }

  return false;
}
export function notIn(columnValue: any, targetValue: any): boolean | undefined {
  if (typeof targetValue === 'string' || typeof targetValue === 'number') {
    return targetValue?.toString()?.includes(columnValue) === false;
  }

  if (Array.isArray(targetValue)) {
    if (targetValue.some(isDatasetLinkValue)) {
      return !targetValue.find((tv) => tv.rowId === columnValue || tv.display === columnValue);
    }
    return targetValue.includes(columnValue) === false; // array
  } else if (!Array.isArray(targetValue) && targetValue === Object(targetValue)) {
    return !(columnValue in targetValue); // object
  }

  return false;
}
export function containsEvery(columnValue: any, targetValue: any): boolean | undefined {
  if (!targetValue) {
    return false;
  }

  return (
    (Array.isArray(targetValue) && targetValue) ||
    (targetValue.startsWith('[') && JSON.parse(targetValue)) ||
    (targetValue && [targetValue])
  ).every((v: any) => columnValue?.includes(v));
}
export function doesNotContain(columnValue: any, targetValue: any): boolean | undefined {
  if (!targetValue) {
    return true;
  }

  return (
    (Array.isArray(targetValue) && targetValue) ||
    (targetValue.startsWith('[') && JSON.parse(targetValue)) ||
    (targetValue && [targetValue])
  ).every((v: any) => columnValue?.indexOf(v) === -1);
}
